import React, { useEffect, useMemo, useState } from 'react';
import * as Icons from "@mui/icons-material";
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
// Removed dummy data; using live API data
import { JMC_COLORS } from '../../styles/colors';
import ProductionModelDialog from './Productionmodel/productionmodel';
import { get, post } from '../../pages/shared/api-service';
import { useNotification } from '../../pages/shared/hooks/notification';

const STATUS_OPTIONS = ['Planned', 'In-Production', 'Completed', 'Cancelled'];

// Normalize API order objects into the UI shape used by the table
function normalizeOrder(o) {
  const orderId = o.orderId || o.id || o.order_id || '';
  const product = o.product || o.productName || o.paper_grade || '-';
  const qty = o.qty ?? o.total_quantity_tons ?? o.quantity ?? 0;
  const productionStatus = o.productionStatus || o.status || 'Planned';
  const dispatchStatus = o.dispatchStatus || o.dispatch_status || 'Pending';
  const orderDate = o.orderDate || o.order_date || o.date || '';
  const customer = o.customer || o.customer_name || '-';
  const supervisorName = o.supervisorName || o.supervisor_name || '-';
  return { orderId, product, qty, productionStatus, dispatchStatus, orderDate, customer, supervisorName };
}

function getChipColor(status) {
  const s = (status || '').toString().toLowerCase();
  switch (s) {
    case 'completed':
      return 'success';
    case 'in-production':
    case 'in production':
      return 'warning';
    case 'planned':
      return 'info';
    case 'cancelled':
    case 'canceled':
      return 'error';
    default:
      return 'default';
  }
}

export default function ProductionOrders() {
  const ReusableTextField = ({ id, name, label, value, onChange, onBlur, touched, error, type = 'text', readOnly = false }) => (
    <TextField
      fullWidth
      size="small"
      id={id}
      name={name}
      label={label}
      value={value ?? ''}
      onChange={onChange}
      onBlur={onBlur}
      error={Boolean(touched) && Boolean(error)}
      helperText={Boolean(touched) && error}
      type={type}
      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      InputProps={{ readOnly }}
      sx={{
        m: 0,
        '& .MuiOutlinedInput-root': { borderRadius: '12px' },
        '& .MuiInputBase-input': { fontSize: 14 },
        '& .MuiInputLabel-root': { fontSize: 14 }
      }}
    />
  );
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [orderBy, setOrderBy] = useState('order_date');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();


  const orderOptions = useMemo(() => orders?.map(o => ({ value: o.order_id || o.orderId || o.id, label: `${o.order_id || o.orderId || o.id} - ${o.paper_grade || o.product || '-'}` })), [orders]);
  const findOrderById = (id) => orders.find(o => (o.order_id || o.orderId) === id);
  const fieldBoxSx = { width: { xs: '100%', sm: 'calc(50% - 12px)' } };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const src = Array.isArray(orders) ? orders : [];
    let list = src.filter(o =>
      String(o.order_id || o.orderId || '').toLowerCase().includes(q) ||
      String(o.paper_grade || o.product || '').toLowerCase().includes(q) ||
      String(o.customer_name || o.customer || '').toLowerCase().includes(q)
    );
    if (statusFilter !== 'All') list = list.filter(o => (o.status || o.productionStatus || '').toString().toLowerCase() === statusFilter.toLowerCase());
    return list;
  }, [orders, search, statusFilter]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      let va = a[orderBy];
      let vb = b[orderBy];
      if (orderBy === 'order_date' || orderBy === 'orderDate' || orderBy === 'target_completion_date') { va = new Date(va).getTime(); vb = new Date(vb).getTime(); }
      if (va < vb) return order === 'asc' ? -1 : 1;
      if (va > vb) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, orderBy, order]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage; return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleSort = (key) => {
    if (orderBy === key) setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else { setOrderBy(key); setOrder('asc'); }
  };

  const openCreate = () => { setEditRow(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditRow(row); setDialogOpen(true); };
  const handleDelete = (row) => {
    // simple confirm
    if (window.confirm(`Delete order ${row.order_id || row.orderId}?`)) {
      setOrders(prev => prev.filter(o => (o.order_id || o.orderId) !== (row.order_id || row.orderId)));
    }
  };

  const initialValues = editRow ? {
    order_id: editRow.order_id ?? editRow.order_id ?? '',
    customer_name: editRow.customer_name ?? editRow.customer ?? '',
    paper_grade: editRow.paper_grade ?? editRow.product ?? '',
    gsm: editRow.gsm ?? '',
    width: editRow.width ?? '',
    diameter: editRow.diameter ?? '',
    total_quantity_tons: editRow.total_quantity_tons ?? editRow.qty ?? '',
    dispatch_value: editRow.dispatch_value ?? '',
    additional_notes: editRow.additional_notes ?? '',
    order_date: editRow.order_date ?? editRow.orderDate ?? '',
    target_completion_date: editRow.target_completion_date ?? '',
    status: editRow.status ?? editRow.productionStatus ?? 'Planned',
  } : {
    // order_id: '',
    customer_name: '',
    paper_grade: '',
    gsm: '',
    width: '',
    diameter: '',
    total_quantity_tons: '',
    dispatch_value: '',
    additional_notes: '',
    order_date: '',
    target_completion_date: '',
    status: 'Planned',
  };

  const schema = Yup.object().shape({
    // order_id: no validation required per request
    // customer_name: no validation required per request
    paper_grade: Yup.string().required('Required'),
    gsm: Yup.number().typeError('Enter a valid number').moreThan(0, 'Must be > 0').required('Required'),
    width: Yup.number().typeError('Enter a valid number').moreThan(0, 'Must be > 0').required('Required'),
    diameter: Yup.number().typeError('Enter a valid number').moreThan(0, 'Must be > 0').required('Required'),
    total_quantity_tons: Yup.number().typeError('Enter a valid number').moreThan(0, 'Must be > 0').required('Required'),
    dispatch_value: Yup.number().typeError('Enter a valid number').min(0, 'Must be ≥ 0').required('Required'),
    additional_notes: Yup.string().max(300, 'Max 300 chars'),
    order_date: Yup.date().required('Required'),
    target_completion_date: Yup.date().required('Required'),
    status: Yup.string().oneOf(STATUS_OPTIONS).required('Required'),
  });

  const handleSubmit = (values, { resetForm }) => {
    console.log('[ProductionOrders] Save:', values);
    postProductionOrders(values);

  };

  const postProductionOrders = async (values) => {
    try {
      const payload = { ...values, status: (values.status || '').toString().toLowerCase() };
      const response = await post('/api/orders', payload);

      console.log("postProductionOrders", response);
      showNotification('Order Created successfully!', 'success', 3000);
      // resetForm();
      setDialogOpen(false);
      // Refresh from GET to ensure latest server state
      await getProductionOrders();
    } catch (err) {
      console.error("getProductionOrders", err);
      showNotification(err.message, 'error', 3000);

    } finally {
      setLoading(false);
    }
  }

  const getProductionOrders = async () => {
    try {
      setLoading(true);
      const response = await get('/api/orders');
      // console.log("getProductionOrders", response.orders);
      setOrders(response.orders);
    } catch (err) {
      console.error("getProductionOrders", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getProductionOrders();
  }, [])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box >
      <Grid container alignItems="center" justifyContent="space-between"  >
        <Grid item>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0 }}>Production Orders</Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<Icons.Add />}
            onClick={openCreate}
            sx={{
              backgroundColor: JMC_COLORS.production,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { backgroundColor: JMC_COLORS.production, filter: 'brightness(0.9)' }
            }}
          >
            New Order
          </Button>
        </Grid>
      </Grid>

      <Paper elevation={2} sx={{ p: 1.5, my: 2, borderRadius: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={search}
              onChange={(e) => { setPage(0); setSearch(e.target.value); }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                minWidth: '160px',
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                '& .MuiInputBase-input': { fontSize: 14 },
                '& .MuiInputLabel-root': { fontSize: 14 }
              }}
            >
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => { setPage(0); setStatusFilter(e.target.value); }}
              >
                <MenuItem sx={{ fontSize: '14px' }} value="All">All</MenuItem>
                {STATUS_OPTIONS?.map(s => <MenuItem sx={{ fontSize: '14px' }} key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'transparent' : '#fafcff', borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'order_id' ? order : false}>
                <TableSortLabel active={orderBy === 'order_id'} direction={order} onClick={() => handleSort('order_id')}>Order ID</TableSortLabel>
              </TableCell>
              <TableCell>Customer Name</TableCell>
              <TableCell>Paper Grade</TableCell>
              <TableCell>GSM</TableCell>
              <TableCell>Diameter</TableCell>
              <TableCell sortDirection={orderBy === 'total_quantity_tons' ? order : false}>
                <TableSortLabel active={orderBy === 'total_quantity_tons'} direction={order} onClick={() => handleSort('total_quantity_tons')}>Total Qty (Tons)</TableSortLabel>
              </TableCell>
              <TableCell>Remaining Qty</TableCell>
              <TableCell>Dispatch Value</TableCell>
              <TableCell sortDirection={orderBy === 'order_date' ? order : false}>
                <TableSortLabel active={orderBy === 'order_date'} direction={order} onClick={() => handleSort('order_date')}>Order Date</TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'target_completion_date' ? order : false}>
                <TableSortLabel active={orderBy === 'target_completion_date'} direction={order} onClick={() => handleSort('target_completion_date')}>Target Completion</TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged?.map(row => (
              <TableRow key={row.order_id || row.orderId} hover>
                <TableCell>{row.order_id || row.orderId}</TableCell>
                <TableCell>{row.customer_name || row.customer || '-'}</TableCell>
                <TableCell>{row.paper_grade || row.product || '-'}</TableCell>
                <TableCell>{row.gsm ?? '-'}</TableCell>
                <TableCell>{row.diameter ?? '-'}</TableCell>
                <TableCell>{row.total_quantity_tons ?? row.qty ?? '-'}</TableCell>
                <TableCell>{row.remaining_quantity_tons ?? '-'}</TableCell>
                <TableCell>{row.dispatch_value ?? '-'}</TableCell>
                <TableCell>{row.order_date || row.orderDate || '-'}</TableCell>
                <TableCell>{row.target_completion_date || '-'}</TableCell>
                <TableCell>
                  <Chip label={row.status || row.productionStatus} color={getChipColor(row.status || row.productionStatus)} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    <IconButton size="small" onClick={() => openEdit(row)}>
                      <Icons.Edit fontSize="small" />
                    </IconButton>

                    <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                      <Icons.Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>

              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sorted.length}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[5, 10, 25]}
      />

      {dialogOpen && (
        <ProductionModelDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          mode="production"
          titleNew="New Order"
          titleEdit="Edit Order"
          initialValues={initialValues}
        validationSchema={schema}
          onSubmit={(vals, helpers) => { handleSubmit(vals, helpers); }}
          orderOptions={orderOptions}
        statusOptions={STATUS_OPTIONS}
          shifts={[]}
          findOrderById={findOrderById}
        />
      )
      }

    </Box>
  );
}





